import type { ReactNode } from "react";

import ListTable, { type ListTableColumn } from "@buildpy/ui/list-table";
import Pill, { type PillTone } from "@buildpy/ui/pill";
import ResponsiveList from "@/components/responsive-list";
import { TableCell, TableRow } from "@buildpy/ui/table";
import { EMPTY_MARK } from "@buildpy/core/lib/format";
import { cn } from "@/lib/utils";
import {
  phaseSchedule,
  phaseScheduleText,
  type PhaseScheduleKind,
  REASON_KINDS,
  scheduleToneKind,
} from "@buildpy/core/modules/project/phase-schedule";
import { PROJECT_PHASE_STATUS_LABEL, type ProjectPhase } from "@buildpy/core/modules/project/types";
import PhaseCard from "@/modules/project/ui/phase-card";
import PhaseReasonEditor from "@/modules/project/ui/phase-reason-editor";
import { PHASE_STATUS_CLASS } from "@/modules/project/ui/phase-status-class";
import PhaseStatusControl from "@/modules/project/ui/phase-status-control";

// 판정 배지 톤 — 지연은 위험 · 착수 지연은 주의(보류색, 끝날까지 지나면 위험) · 조기 완료는 완료색 · 나머지는 조용히.
const SCHEDULE_TONE: Record<PhaseScheduleKind, PillTone> = {
  NO_PLAN: "neutral",
  NORMAL: "neutral",
  START_LATE: "hold",
  LATE: "danger",
  EARLY: "done",
  NO_RECORD: "neutral",
};

const range = (from: string | null, to: string | null): string =>
  from === null && to === null ? EMPTY_MARK : `${from ?? EMPTY_MARK} ~ ${to ?? EMPTY_MARK}`;

// 표 칸용 기간 — 좁으면(lg 의 왼쪽 칸) 「시작 ~」 뒤에서만 두 줄로 꺾인다. 넓으면 한 줄.
const rangeLines = (from: string | null, to: string | null): ReactNode =>
  from === null && to === null ? (
    EMPTY_MARK
  ) : (
    <>
      <span className="whitespace-nowrap">{`${from ?? EMPTY_MARK} ~`}</span>{" "}
      <span className="whitespace-nowrap">{to ?? EMPTY_MARK}</span>
    </>
  );

const PHASE_COLUMNS: ListTableColumn[] = [
  { label: "순서", className: "w-12" },
  { label: "공정" },
  { label: "일수", align: "right" },
  { label: "계획" },
  { label: "실제" },
  { label: "상태" },
];

interface Props {
  phases: readonly ProjectPhase[];
  // 공정 코드 → 그 회사에게 보이는 이름. 없으면 코드를 보인다.
  names: ReadonlyMap<string, string>;
  // 상태 버튼을 붙일지 — 서버가 현장·계약 상태로 정해 넘긴다(시공중 · 체결).
  editableProjectId?: string | null;
  // 판정 기준일(KST 오늘) — 서버가 넘긴다.
  today: string;
  // 사유를 고칠 수 있는 현장 — 착공했고 안 닫힌 현장이면 id, 아니면 null.
  reasonEditableProjectId?: string | null;
}

// 공정 타임라인 — 순서대로 한 줄씩 계획 대비 실제. 날짜는 `YYYY-MM-DD` 그대로 보인다(시간대 변환 없음).
const PhaseTimeline = ({ phases, names, editableProjectId = null, today, reasonEditableProjectId = null }: Props) => {
  const ordered = [...phases].sort((a, b) => a.sort_order - b.sort_order || a.id.localeCompare(b.id));
  // 표와 폰 카드가 같은 판정을 쓴다 — 한 번만 계산한다.
  const rows = ordered.map((phase, index) => {
    const schedule = phaseSchedule(phase, today);
    return {
      phase,
      order: index + 1,
      name: names.get(phase.phase_code) ?? phase.phase_code,
      schedule,
      tone: SCHEDULE_TONE[scheduleToneKind(phase, schedule, today)],
      baselineMoved:
        (phase.baseline_start_date !== null || phase.baseline_end_date !== null) &&
        (phase.baseline_start_date !== phase.start_date || phase.baseline_end_date !== phase.end_date),
      showsReason: REASON_KINDS.includes(schedule.kind) || phase.schedule_reason !== "",
    };
  });
  const reasonOf = ({ phase, name }: (typeof rows)[number], className: string, testIdPrefix?: string) => (
    <PhaseReasonEditor
      className={className}
      editable={reasonEditableProjectId !== null}
      phaseId={phase.id}
      phaseName={name}
      projectId={reasonEditableProjectId ?? phase.project_id}
      reason={phase.schedule_reason}
      testIdPrefix={testIdPrefix}
    />
  );
  const emptyText = "공정표가 없습니다. 견적에 공정을 넣으면 다음 현장부터 이어받습니다.";

  const table = (
    <ListTable columns={PHASE_COLUMNS} empty={emptyText}>
      {rows.map((row) => {
        const { phase, name, schedule } = row;
        return (
          <TableRow key={phase.id} data-testid={`phase-row-${phase.id}`}>
            <TableCell className="px-3 py-2 tabular-nums text-muted-foreground">{row.order}</TableCell>
            <TableCell className="px-3 py-2">
              <span className="whitespace-nowrap">{name}</span>
              {phase.note && <span className="ml-2 text-xs text-muted-foreground">{phase.note}</span>}
            </TableCell>
            <TableCell className="px-3 py-2 text-right tabular-nums">{phase.duration_days}일</TableCell>
            <TableCell className="px-3 py-2 tabular-nums">
              {rangeLines(phase.start_date, phase.end_date)}
              {row.baselineMoved && (
                <span className="block text-xs text-muted-foreground" data-testid={`phase-${phase.id}-baseline`}>
                  최초 {rangeLines(phase.baseline_start_date, phase.baseline_end_date)}
                </span>
              )}
            </TableCell>
            <TableCell className="px-3 py-2 tabular-nums" data-testid={`phase-${phase.id}-actual`}>
              {rangeLines(phase.actual_start_date, phase.actual_end_date)}
            </TableCell>
            <TableCell className="px-3 py-2 text-sm">
              <span className="flex flex-wrap items-center gap-1.5">
                <span className={cn("whitespace-nowrap", PHASE_STATUS_CLASS[phase.status])}>
                  {PROJECT_PHASE_STATUS_LABEL[phase.status]}
                </span>
                {schedule.kind !== "NO_PLAN" && (
                  <span data-testid={`phase-${phase.id}-schedule`}>
                    <Pill className="whitespace-nowrap" tone={row.tone}>
                      {phaseScheduleText(schedule)}
                    </Pill>
                  </span>
                )}
              </span>
              {row.showsReason && reasonOf(row, "mt-1")}
              {editableProjectId && (
                <PhaseStatusControl
                  schedule={phase}
                  className="mt-1"
                  phaseId={phase.id}
                  phaseName={name}
                  projectId={editableProjectId}
                  status={phase.status}
                />
              )}
            </TableCell>
          </TableRow>
        );
      })}
    </ListTable>
  );

  const cards = rows.map((row) => {
    const { phase, schedule } = row;
    return (
      <PhaseCard
        key={phase.id}
        editableProjectId={editableProjectId}
        name={row.name}
        order={row.order}
        phase={phase}
        testIdPrefix={`phase-card-${phase.id}`}>
        {phase.note && <p className="break-words text-xs text-muted-foreground">{phase.note}</p>}
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs tabular-nums">
          <dt className="text-muted-foreground">일수</dt>
          <dd>{phase.duration_days}일</dd>
          <dt className="text-muted-foreground">실제</dt>
          <dd data-testid={`phase-card-${phase.id}-actual`}>{range(phase.actual_start_date, phase.actual_end_date)}</dd>
          {row.baselineMoved && (
            <>
              <dt className="text-muted-foreground">최초</dt>
              <dd>{range(phase.baseline_start_date, phase.baseline_end_date)}</dd>
            </>
          )}
        </dl>
        {schedule.kind !== "NO_PLAN" && (
          <span className="flex" data-testid={`phase-card-${phase.id}-schedule`}>
            <Pill className="whitespace-nowrap" tone={row.tone}>
              {phaseScheduleText(schedule)}
            </Pill>
          </span>
        )}
        {row.showsReason && reasonOf(row, "", `phase-card-${phase.id}`)}
      </PhaseCard>
    );
  });

  return <ResponsiveList cards={cards} empty={emptyText} label="공정표" table={table} testId="phase-cards" />;
};

export default PhaseTimeline;
